import styled from 'foundations-theming/utils';
import { Component } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import debounce from 'transmute/debounce';
import { fromJS, Map as ImmutableMap } from 'immutable';
import partial from 'react-utils/partial';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Overlay } from 'foundations-components/transitional/components/Overlay';
import { OptimisticNanoProgress } from 'foundations-components/transitional/components/OptimisticNanoProgress';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import ImageLinkPopover from './components/ImageLinkPopover';
import { getFileInfo } from './api/FileManagerApi';
import { getSignedSrc } from './utils/Images';
import ResizeableThumbnailDragHandleCorners from './components/ResizeableThumbnailDragHandleCorners';
import ResizeableThumbnailOutline from './components/ResizeableThumbnailOutline';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { LinkIcon } from 'foundations-assets/icons/components/LinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isLegacySignedUrl = url => url && url.includes('rich_text_hidden_files');
const getLegacySignedUrlBasePath = signedUrl => {
  const pathStartIndex = signedUrl.indexOf('rich_text_hidden_files');
  const pathEndIndex = signedUrl.indexOf('?Expires');
  return signedUrl.slice(pathStartIndex, pathEndIndex);
};
const ImageLinkButton = props => {
  return /*#__PURE__*/_jsx("span", {
    onClick: props.togglePopover,
    children: /*#__PURE__*/_jsx(LinkIcon, {})
  });
};
const ResizeableThumbnailPreview = styled.img.withConfig({
  displayName: "ImageComponent__ResizeableThumbnailPreview"
})(["block-size:", "px;inline-size:", "px;opacity:0.3;position:absolute;top:-3px;left:-3px;display:", ";"], props => props.height, props => props.width, props => props.display === false ? 'none' : 'block');
const ImageWrapper = styled.div.withConfig({
  displayName: "ImageComponent__ImageWrapper"
})(["display:inline-flex;position:relative;"]);
const DecoratedLinkButton = ImageLinkPopover(ImageLinkButton);
class ImageComponent extends Component {
  constructor(props) {
    super(props);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'draftContext' does not exist on type 'Im... Remove this comment to see the full error message
    this.img = null;
    this.linkPopover = null;
    this.parent = null;
    this.debouncedUpdateData = debounce(300, (...args) => {
      const {
        useProsemirror,
        prosemirrorProps
      } = this.props;
      if (useProsemirror) {
        prosemirrorProps.updateImage(...args);
      } else {
        // @ts-expect-error ts-migrate(2556) FIXME: A spread argument must either have a tuple type or... Remove this comment to see the full error message
        this.updateData(...args);
      }
    });
    this.handleClick = () => {
      if (this.state.isInitiallyReadOnly) {
        return;
      }
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      this.props.onFocus();
      this.ensureValidDimensions();
    };
    this.handleBlur = () => {
      var _this$linkPopover;
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      this.handleLinkPopoverClose();
      (_this$linkPopover = this.linkPopover) === null || _this$linkPopover === void 0 || _this$linkPopover.clearInput();
    };
    this.updateData = data => {
      const {
        block
      } = this.props;
      const {
        getEditorState,
        onChange
      } = this.props.draftContext;
      const editorState = getEditorState();
      const {
        image = {},
        link = {}
      } = data;
      const oldData = this.getData();
      const updatedBlockData = oldData.mergeDeep({
        image,
        link
      });

      // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'changeBlockData'.
      const updatedEditorState = changeBlockData({
        editorState,
        block,
        updatedBlockData
      });
      onChange(updatedEditorState);
    };
    this.getData = () => {
      const blockData = this.props.block.getData();
      const isFormatted = ImmutableMap.isMap(blockData.get('image'));
      if (isFormatted) {
        return blockData;
      }
      const rawData = blockData.toObject();
      // the `convertFromRaw` function supplied by draft-js does not
      // deeply convert block data to Immutable objects (it simply
      // calls the Map constructor on `block.data`), so we do this
      // hack to make sure all our data is in Immutable form
      const deeplyConverted = fromJS(rawData);
      return deeplyConverted;
    };
    this.updateSize = (width, height) => {
      if (width > 0 && height > 0) {
        const newData = {
          image: {
            width,
            height
          }
        };
        this.debouncedUpdateData(newData);
        this.setState({
          width: Math.round(width),
          height: Math.round(height),
          previewWidth: Math.round(width),
          previewHeight: Math.round(height)
        });
      }
    };
    this.ensureValidDimensions = () => {
      const {
        width,
        height,
        previewWidth,
        previewHeight
      } = this.state;
      if (!width || !height) {
        var _this$parent;
        const parentRect = (_this$parent = this.parent) === null || _this$parent === void 0 ? void 0 : _this$parent.getBoundingClientRect();
        if (!parentRect) {
          return;
        }
        const parentWidth = parentRect.width;
        const parentHeight = parentRect.height;
        this.setState({
          ratio: parentWidth / parentHeight
        });
        this.updateSize(parentWidth, parentHeight);
      }
      if (!previewWidth || !previewHeight) {
        const {
          width: imgWidth,
          height: imgHeight
        } = this.getImageDimensions();
        this.setState({
          previewWidth: Math.round(imgWidth),
          previewHeight: Math.round(imgHeight)
        });
      }
    };
    this.getImageRatio = () => {
      const {
        width,
        height
      } = this.getImageDimensions();
      const ratio = width / height;
      this.setState({
        ratio,
        isPct: false
      });
      return ratio;
    };
    this.getImageDimensions = () => {
      var _this$img$getBounding, _this$img;
      const {
        width,
        height
      } = (_this$img$getBounding = (_this$img = this.img) === null || _this$img === void 0 ? void 0 : _this$img.getBoundingClientRect()) !== null && _this$img$getBounding !== void 0 ? _this$img$getBounding : {
        width: 1,
        height: 1
      };
      return {
        width,
        height
      };
    };
    this.getNewBounds = e => {
      const {
        isPct,
        originalDragPosition,
        invertHorizontal,
        invertVertical
      } = this.state;
      let {
        width,
        height
      } = this.state;
      let {
        ratio
      } = this.state;
      if (isPct) {
        ratio = this.getImageRatio();
      }

      // Always reset to use the measured width and height
      ({
        width,
        height
      } = this.getImageDimensions());
      if (!originalDragPosition) {
        return {
          newWidth: width,
          newHeight: height
        };
      }
      const xDiff = originalDragPosition.clientX - e.clientX;
      const yDiff = originalDragPosition.clientY - e.clientY;

      //Calculate the new bounds for the image for both the X and Y distance dragged
      const newWidthX = invertHorizontal ? width + xDiff : width - xDiff;
      const newHeightX = ratio ? Math.round(newWidthX / ratio) : height;
      const newHeightY = invertVertical ? height + yDiff : height - yDiff;
      const newWidthY = ratio ? Math.round(newHeightY * ratio) : width;
      if (newWidthX > newWidthY) {
        return {
          newWidth: newWidthX,
          newHeight: newHeightX
        };
      } else {
        return {
          newWidth: newWidthY,
          newHeight: newHeightY
        };
      }
    };
    this.handleDefault = e => {
      e.preventDefault();
      if (e.type === 'click') {
        e.stopPropagation();
      }
    };
    this.handleMouseDown = (e, invertHorizontal, invertVertical) => {
      const {
        width,
        height
      } = this.getImageDimensions();
      this.setState({
        dragging: true,
        originalDragPosition: {
          clientX: e.clientX,
          clientY: e.clientY
        },
        invertHorizontal,
        invertVertical,
        previewWidth: width,
        previewHeight: height
      });
      this.handleDefault(e);
    };
    this.handleMouseUp = e => {
      const {
        dragging
      } = this.state;
      if (dragging) {
        const {
          newWidth,
          newHeight
        } = this.getNewBounds(e);
        this.updateSize(newWidth, newHeight);
      }
      this.setState({
        dragging: false,
        originalDragPosition: null,
        invertHorizontal: null,
        invertVertical: null,
        previewWidth: null,
        previewHeight: null
      });
      this.forceUpdate(() => {
        this.ensureValidDimensions();
      });
      this.handleDefault(e);
    };
    this.handleMouseMove = e => {
      const {
        dragging
      } = this.state;
      if (dragging) {
        const {
          newWidth,
          newHeight
        } = this.getNewBounds(e);
        this.setState({
          previewWidth: newWidth,
          previewHeight: newHeight
        });
      }
    };
    this.handleButtonClick = (newWidth, e) => {
      const {
        isPct
      } = this.state;
      let {
        ratio
      } = this.state;
      this.handleDefault(e);
      if (isPct) {
        ratio = this.getImageRatio();
      }
      const newHeight = ratio ? Math.round(newWidth / ratio) : this.state.height;
      this.updateSize(newWidth, newHeight);
      this.setState({
        dragging: false,
        originalDragPosition: null,
        invertHorizontal: null,
        invertVertical: null,
        previewWidth: null,
        previewHeight: null
      });
      this.forceUpdate(() => {
        this.ensureValidDimensions();
      });
    };
    this.handleLinkPopoverOpen = () => {
      this.props.overrideKeyDown();
      this.setState({
        linkPopoverOpen: true
      });
    };
    this.handleLinkPopoverClose = () => {
      this.props.restoreKeyDown();
      this.setState({
        linkPopoverOpen: false
      });
    };
    this.handleLinkPopoverOpenChange = e => {
      this.setState({
        linkPopoverOpen: e.target.value
      });
    };
    this.handleLinkChange = newData => {
      this.debouncedUpdateData({
        link: newData
      });
      this.setState(Object.assign({
        linkPopoverOpen: false
      }, newData));
      this.props.onFocus();
    };
    this.handleRemove = () => {
      this.handleLinkChange({
        url: null,
        isTargetBlank: null
      });
    };
    this.renderResizeTools = previewImgSrc => {
      const {
        selected
      } = this.props;
      const {
        width,
        height,
        dragging,
        isPct,
        previewWidth,
        previewHeight,
        invertHorizontal,
        invertVertical
      } = this.state;
      const hasWidth = Boolean(width && width !== 'NaN' || previewWidth);
      const hasHeight = Boolean(height && height !== 'NaN' || previewHeight);
      const hasDimensions = hasWidth && hasHeight;
      return /*#__PURE__*/_jsx(View, Object.assign({}, isPct ? {
        inlineSize: width,
        blockSize: height,
        position: 'absolute'
      } : {}, {
        children: /*#__PURE__*/_jsxs(ResizeableThumbnailOutline, {
          height: previewHeight || height,
          width: previewWidth || width,
          invertHorizontal: invertHorizontal,
          invertVertical: invertVertical,
          selected: selected,
          children: [/*#__PURE__*/_jsx(ResizeableThumbnailDragHandleCorners, {
            selected: selected,
            handleMouseDown: this.handleMouseDown
          }), /*#__PURE__*/_jsx(ResizeableThumbnailPreview, {
            src: previewImgSrc,
            height: previewHeight || height,
            width: previewWidth || width
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            ,
            display: dragging
          }), selected && hasDimensions && /*#__PURE__*/_jsx(Badge, {
            use: "pantera",
            position: "absolute",
            margin: "200",
            insetBlockStart: "0",
            insetInlineEnd: "0",
            children: `${previewWidth || width} x ${previewHeight || height}`
          })]
        })
      }));
    };
    this.renderProgressOverlay = (isTemporary, uploadPercent) => {
      if (!isTemporary || uploadPercent >= 100) {
        return null;
      }
      const {
        width
      } = this.state;
      return /*#__PURE__*/_jsxs(Overlay, {
        contextual: true,
        width: width,
        use: "light",
        children: [/*#__PURE__*/_jsx(OptimisticNanoProgress, {
          value: uploadPercent
        }), /*#__PURE__*/_jsx(TFlex, {
          alignItems: "center",
          justifyContent: "center",
          inset: "0",
          position: "absolute",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {})
        })]
      });
    };
    const {
      useProsemirror: _useProsemirror,
      prosemirrorProps: _prosemirrorProps,
      draftContext
    } = props;
    const isInitiallyReadOnly = _useProsemirror ? false : draftContext.getReadOnly();
    let _width;
    let _height;
    if (_useProsemirror) {
      _width = _prosemirrorProps.width;
      _height = _prosemirrorProps.height;
    } else {
      const {
        image
      } = this.getData().toObject();
      _width = image.toObject().width;
      _height = image.toObject().height;
    }
    const isPercentageBasedWidth = _width && typeof _width === 'string' && _width.indexOf('%') !== -1;
    let parsedWidth;
    let parsedHeight;
    let _ratio;
    if (isPercentageBasedWidth) {
      parsedWidth = _width;
      parsedHeight = _height;
    } else {
      parsedWidth = parseInt(_width, 10);
      parsedHeight = parseInt(_height, 10);
      if (!isNaN(parsedWidth) && !isNaN(parsedHeight)) {
        _ratio = parsedWidth / parsedHeight;
      }
    }
    this.state = {
      ratio: _ratio,
      isPct: isPercentageBasedWidth,
      isInitiallyReadOnly,
      width: parsedWidth,
      height: parsedHeight,
      dragging: false,
      linkPopoverOpen: false,
      originalDragPosition: null,
      invertHorizontal: null,
      invertVertical: null,
      previewWidth: null,
      previewHeight: null
    };
  }
  componentDidMount() {
    const {
      useProsemirror,
      prosemirrorProps
    } = this.props;
    const src = useProsemirror ? prosemirrorProps.src : this.getData().getIn(['image', 'src']);
    if (isLegacySignedUrl(src)) {
      const fileManagerPath = getLegacySignedUrlBasePath(src);
      getFileInfo(fileManagerPath).then(({
        id
      }) => {
        const newSrc = getSignedSrc(id);
        const updatedData = {
          image: {
            src: newSrc
          }
        };
        if (useProsemirror) {
          prosemirrorProps.updateImage(updatedData);
        } else {
          this.updateData(updatedData);
        }
      }).catch(e => {
        console.error(e);
      });
    }
    this.ensureValidDimensions();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.selected === true && this.props.selected === false) {
      this.handleBlur();
    }
  }
  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  // This function gets the new bounds (width and height) of an image given
  // the original bounds, where you started dragging from, where you
  // finished dragging (gotten from the event), the ratio of the image, and
  // if we should invert the horizontal and the vertical dimensions.
  // the Invert variables are set based on which corner you are dragging from
  // (Values found in the ResizeableThumbnailDragHandleCorners file)
  // Top Left: Both Inverted
  // Top Right: Horizontal Inverted
  // Bottom Left: Vertical Inverted
  // Bottom Right: Neither Inverted
  render() {
    var _this$img2, _this$img3;
    const {
      useProsemirror,
      prosemirrorProps,
      selected
    } = this.props;
    const {
      width,
      height,
      isPct,
      linkPopoverOpen,
      dragging
    } = this.state;
    const {
      image,
      link
    } = useProsemirror ? {} : this.getData().toObject();
    let url;
    let isTargetBlank;
    let isNoFollow;
    if (link) {
      url = link.get('url');
      isTargetBlank = link.get('isTargetBlank');
      isNoFollow = link.get('isNoFollow');
    }
    if (useProsemirror && prosemirrorProps !== null && prosemirrorProps !== void 0 && prosemirrorProps.link) {
      url = prosemirrorProps.link.url;
      isTargetBlank = prosemirrorProps.link.isTargetBlank;
      isNoFollow = prosemirrorProps.link.isNoFollow;
    }
    const align = prosemirrorProps === null || prosemirrorProps === void 0 ? void 0 : prosemirrorProps.align;
    const src = useProsemirror ? prosemirrorProps.src : this.getData().getIn(['image', 'src']);
    const isTemporary = useProsemirror ? !!prosemirrorProps.isTemporary : !!this.getData().getIn(['image', 'isTemporary']);
    const uploadPercent = useProsemirror ? prosemirrorProps.uploadPercent : this.getData().getIn(['image', 'uploadPercent']);
    const sizes = [250, 400];
    let buttons = [];
    const naturalDimensions = (_this$img2 = this.img) !== null && _this$img2 !== void 0 && _this$img2.naturalWidth && (_this$img3 = this.img) !== null && _this$img3 !== void 0 && _this$img3.naturalHeight ? {
      width: this.img.naturalWidth,
      height: this.img.naturalHeight
    } : null;
    const computedDimensions = useProsemirror ? prosemirrorProps.originalDimensions : image.get('originalDimensions');
    const originalDimensions = naturalDimensions || computedDimensions;
    if (originalDimensions && originalDimensions.width) {
      buttons.push( /*#__PURE__*/_jsx(Link, {
        disabled: originalDimensions.width === width,
        onClick: partial(this.handleButtonClick, originalDimensions.width),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "draftPlugins.imagePlugin.resetSize"
        })
      }, "image-resize-button-original"));
    }
    buttons = buttons.concat(sizes.map(size => {
      return /*#__PURE__*/_jsx(Link, {
        disabled: size === width,
        onClick: partial(this.handleButtonClick, size),
        children: `${size}px`
      }, `image-resize-button-${size}`);
    }));
    buttons.push( /*#__PURE__*/_jsx(Link, {
      children: /*#__PURE__*/_jsx(DecoratedLinkButton, {
        ref: c => {
          this.linkPopover = c;
        },
        open: linkPopoverOpen,
        onOpenChange: this.handleLinkPopoverOpenChange,
        url: url,
        isTargetBlank: isTargetBlank,
        isNoFollow: isNoFollow,
        onOpen: this.handleLinkPopoverOpen,
        onCancel: this.handleLinkPopoverClose,
        onConfirm: this.handleLinkChange,
        onRemove: this.handleRemove
      })
    }, "image-link-button"));
    const imageStyle = isPct ? {
      width,
      height
    } : {};
    const imageAttrs = isPct ? {} : {
      width,
      height
    };
    const imageProps = Object.assign({
      style: Object.assign({}, imageStyle)
    }, imageAttrs, {
      src
    });
    return /*#__PURE__*/_jsx(View, {
      textAlign: align,
      ref: this.props.forwardRef,
      children: /*#__PURE__*/_jsxs(ImageWrapper, {
        ref: c => {
          this.parent = c;
        },
        onClick: this.handleClick,
        children: [this.renderProgressOverlay(isTemporary, uploadPercent), /*#__PURE__*/_jsx(Tooltip, {
          open: selected && !dragging,
          use: "longform",
          paddingBlock: "200",
          paddingInline: "300",
          title: /*#__PURE__*/_jsx(List, {
            inline: true,
            use: "inline-divided",
            padding: "0",
            children: buttons.map(button => /*#__PURE__*/_jsx(ListItem, {
              children: button
            }, button.key))
          }),
          children: /*#__PURE__*/_jsx("img", Object.assign({
            ref: c => {
              this.img = c;
            }
          }, imageProps))
        }), this.renderResizeTools(src)]
      })
    });
  }
}
ImageComponent.displayName = 'ImageComponent';
export const ProsemirrorImageComponent = ImageComponent;